import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import QuestionCard from '../components/QuestionCard';
import ScoreDisplay from '../components/ScoreDisplay';
import ProgressBar from '../components/ProgressBar';
import { Question } from '../types';
import { useGame } from '../hooks/useGame';
import { useAuth } from '../contexts/AuthContext';
import { saveGameToFirestore } from '../services/game.service';
import questionsData from '../data/questions-all-1000.json';
import { BookOpen, ArrowRight, SkipForward } from 'lucide-react';

const TrainingContainer = styled.div`
  min-height: 100vh;
  height: 100vh;
  background: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => theme.spacing.md};
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-height: 700px) {
    padding: ${({ theme }) => theme.spacing.sm};
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
  flex-shrink: 0;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const TrainingBadge = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.success};
  color: #fff;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-weight: 600;
  font-size: 0.9rem;
`;

const TrainingContent = styled.div`
  max-width: 900px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const QuestionContainer = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.md};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  scroll-behavior: smooth;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.background};
    border-radius: ${({ theme }) => theme.borderRadius.md};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.primary};
    border-radius: ${({ theme }) => theme.borderRadius.md};
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.secondary};
  }

  @media (max-height: 700px) {
    padding: ${({ theme }) => theme.spacing.sm};
  }

  @media (max-width: 767px) {
    padding: ${({ theme }) => theme.spacing.sm};
  }
`;

const Explanation = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border-left: 4px solid ${({ theme }) => theme.colors.accent};
`;

const ExplanationTitle = styled.div`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.accent};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const ExplanationText = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
  line-height: 1.6;
`;

const VerseReference = styled.div`
  font-style: italic;
  color: ${({ theme }) => theme.colors.secondary};
  margin-top: ${({ theme }) => theme.spacing.sm};
  font-weight: 600;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  justify-content: center;
  margin-top: ${({ theme }) => theme.spacing.lg};
  flex-shrink: 0;
  flex-wrap: wrap;

  @media (max-width: 767px) {
    flex-direction: column;
  }
`;

const NextButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  min-height: 50px;
  min-width: 180px;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  color: ${({ theme }) => theme.colors.text};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 767px) {
    min-height: 48px;
    font-size: 1rem;
    width: 100%;
  }
`;

const SkipButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  min-height: 50px;
  min-width: 180px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  border: 2px solid ${({ theme }) => theme.colors.textSecondary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 767px) {
    min-height: 48px;
    font-size: 1rem;
    width: 100%;
  }
`;

const TrainingScreen: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);
  const hasStartedRef = useRef(false);

  // Initialize game with useGame hook (training mode - no time limit)
  const {
    currentQuestion,
    currentQuestionIndex,
    score,
    streak,
    longestStreak,
    totalQuestions,
    isGameActive,
    submitAnswer,
    nextQuestion,
    startGame,
  } = useGame({
    questions: questionsData as Question[],
    mode: 'training',
    category: 'all',
    difficulty: 'mixed',
    questionsPerGame: 10,
    timePerQuestion: 0, // No time limit in training mode
    onGameComplete: async (result) => {
      // Save game to Firestore
      if (currentUser) {
        try {
          const questions = (questionsData as Question[]).slice(0, 10);
          await saveGameToFirestore(currentUser.uid, {
            mode: 'training',
            category: 'all',
            difficulty: 'mixed',
            questions: questions.map(q => q.id),
            answers: result.answers,
            score: result.score,
            accuracy: result.accuracy,
            totalTime: result.totalTime,
            longestStreak: longestStreak,
          });
        } catch (error) {
          console.error('Error saving game:', error);
        }
      }

      // Navigate to results
      navigate('/results', {
        state: {
          results: {
            score: result.score,
            accuracy: result.accuracy,
            totalTime: result.totalTime,
            correctCount: result.correctCount,
            incorrectCount: result.incorrectCount,
            longestStreak: longestStreak,
            mode: 'training' as const,
          },
        },
      });
    },
  });

  // Start game on mount (only once)
  useEffect(() => {
    if (!hasStartedRef.current) {
      hasStartedRef.current = true;
      startGame();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle answer selection
  const handleAnswerSelect = (index: number) => {
    if (showResult || !currentQuestion) return;

    setSelectedAnswer(index);
    setShowResult(true);

    // Submit answer without time tracking (0ms)
    submitAnswer(index, 0);
  };

  // Handle skip question
  const handleSkip = () => {
    if (showResult) return;

    // Submit as skipped (-1) with 0 time
    setSelectedAnswer(-1);
    setShowResult(true);
    submitAnswer(-1, 0);
  };

  // Handle next question
  const handleNext = () => {
    setSelectedAnswer(null);
    setShowResult(false);
    nextQuestion();
  };

  // Don't render if game hasn't started or no current question
  if (!isGameActive || !currentQuestion) {
    return (
      <TrainingContainer>
        <TrainingContent>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <h2>Loading training mode...</h2>
          </div>
        </TrainingContent>
      </TrainingContainer>
    );
  }

  return (
    <TrainingContainer>
      <TrainingContent>
        <Header>
          <TrainingBadge>
            <BookOpen size={18} />
            Training Mode - Take Your Time
          </TrainingBadge>
          <ScoreDisplay score={score} streak={streak} />
        </Header>

        <ProgressBar current={currentQuestionIndex + 1} total={totalQuestions} />

        <QuestionContainer>
          <QuestionCard
            question={currentQuestion}
            selectedAnswer={selectedAnswer}
            onAnswerSelect={handleAnswerSelect}
            showResult={showResult}
          />

          {showResult && (
            <>
              <Explanation>
                <ExplanationTitle>Explanation</ExplanationTitle>
                <ExplanationText>{currentQuestion.explanation}</ExplanationText>
                <VerseReference>{currentQuestion.verseReference}</VerseReference>
              </Explanation>

              <ButtonGroup>
                <NextButton onClick={handleNext}>
                  {currentQuestionIndex < totalQuestions - 1 ? 'Next Question' : 'Finish Training'}
                  <ArrowRight size={20} />
                </NextButton>
              </ButtonGroup>
            </>
          )}

          {!showResult && (
            <ButtonGroup>
              <SkipButton onClick={handleSkip}>
                Skip Question
                <SkipForward size={20} />
              </SkipButton>
            </ButtonGroup>
          )}
        </QuestionContainer>
      </TrainingContent>
    </TrainingContainer>
  );
};

export default TrainingScreen;
